/**
 * Editable legal content for /privacy and /terms.
 *
 * These pages remain drafts until Australian legal and technical review is complete.
 * After counsel returns finalised wording, edit the strings below — no JSX changes required.
 *
 * Do NOT add specific claims that are not yet approved (storage location, encryption standards,
 * retention periods, regulatory compliance, third-party processor names, on-device processing
 * claims, certification claims).
 */

export const PRIVACY = {
  kicker: 'Privacy',
  title: 'Privacy policy.',
  draftLabel: 'Draft — subject to legal and technical review before publication',
  intro:
    'This draft outlines how Valanti Labs intends to handle personal information when the application is publicly available. It will be finalised after legal and technical review and before public release.',
  lastUpdated:
    'Last updated: this draft is updated as the privacy and technical review progresses. Final dates, retention periods, locations, providers, encryption standards and regulatory references will be confirmed prior to public release.',
  sections: [
    {
      t: 'Company identity',
      b: 'Valanti Labs Pty Ltd (ABN 54 692 520 497), based in Australia, is the entity responsible for the Valanti Labs application and this website.',
    },
    {
      t: 'Contact pathway',
      b: 'Until monitored privacy email addresses are published, privacy enquiries can be sent using the contact form on this site with the topic "Privacy".',
    },
    {
      t: 'Types of information collected',
      b: 'When the application is publicly available, we expect to collect account information you provide, movement videos you record or upload through the workflow available in the release version, movement-analysis outputs derived from those videos, basic technical information about your device, and crash and diagnostic information used to keep the application working. Final categories will be confirmed before public release.',
    },
    {
      t: 'Account information',
      b: 'Information used to create and manage your Valanti Labs account, including your email address. The exact list of fields will be confirmed before public release.',
    },
    {
      t: 'Movement videos',
      b: 'Videos provided through the workflow available in the release version of the application, used for movement analysis. Final handling, storage and retention will be confirmed before publication.',
    },
    {
      t: 'Movement-analysis data',
      b: 'Structured outputs derived from your videos — such as supported observation categories and reliability indications.',
    },
    {
      t: 'Technical information',
      b: 'Technical signals such as device type, operating system version and basic information needed for the application to function and to be supported.',
    },
    {
      t: 'Analytics',
      b: 'We do not currently operate advertising trackers on this website. Any future analytics will be introduced only after the privacy and consent approach has been finalised.',
    },
    {
      t: 'Crash reporting',
      b: 'Crash and diagnostic information used to identify and fix problems. The specific scope, third parties and retention practices will be confirmed before public release.',
    },
    {
      t: 'Device information',
      b: 'Information about the device you use to access Valanti Labs, used for compatibility and support.',
    },
    {
      t: 'Purposes of collection',
      b: 'Information is collected to provide the application and its movement-analysis features, to support users, to improve the product and the safety of the application, and to meet legal obligations.',
    },
    {
      t: 'Legal basis where relevant',
      b: 'Where required by law, processing is based on your consent, on the performance of our agreement with you, and on our legitimate interests in operating a safe, lawful and well-supported product.',
    },
    {
      t: 'Service providers',
      b: 'We may engage third-party service providers to host, process or support the application. Final providers will be disclosed once confirmed.',
    },
    {
      t: 'Data storage',
      b: 'Final data-storage practices — including locations, encryption practices and standards — will be confirmed before public release.',
    },
    {
      t: 'Data retention',
      b: 'We will retain information only for as long as is necessary for the purposes described in this policy and to meet legal obligations. Exact retention periods will be confirmed before public release.',
    },
    {
      t: 'Security',
      b: 'We will use a reasonable combination of organisational and technical measures to protect personal information. We do not claim absolute security.',
    },
    {
      t: 'International processing',
      b: 'Information may be processed outside Australia. Specific locations will be confirmed before public release.',
    },
    {
      t: 'Access and correction',
      b: 'You will be able to request access to and correction of personal information that we hold about you, subject to verification.',
    },
    {
      t: 'Deletion',
      b: 'You will be able to request deletion of your account. See the Account Deletion page for details.',
    },
    {
      t: 'Complaints',
      b: 'If you have a privacy complaint, please contact us using the contact form. You may also have the right to lodge a complaint with a relevant data-protection authority.',
    },
    {
      t: 'Children and age limits',
      b: 'Valanti Labs is not designed for children. The minimum age for use will be confirmed before public release.',
    },
    {
      t: 'Updates to the policy',
      b: 'We will update this policy as the application develops and before public release. Changes will be reflected here with the date last updated.',
    },
  ],
};

export const TERMS = {
  kicker: 'Terms',
  title: 'Terms and conditions.',
  draftLabel: 'Draft — subject to legal review before publication',
  intro:
    'This draft outlines the proposed terms of use for the Valanti Labs application and website. It will be finalised after Australian legal review before public release.',
  lastUpdated:
    'Last updated: this draft is updated as legal review progresses. Final wording will be confirmed prior to public release.',
  sections: [
    { t: 'Eligibility', b: 'Valanti Labs is intended for use by adults. Final age and eligibility requirements will be confirmed before public release.' },
    { t: 'Account registration', b: 'Where account registration is offered, you agree to provide accurate information and to keep your account credentials secure.' },
    { t: 'Acceptable use', b: 'You agree to use Valanti Labs only for lawful purposes and in line with these terms. You will not attempt to misuse, reverse-engineer or interfere with the application.' },
    { t: 'Movement-video uploads', b: 'Where you provide movement videos through the workflow available in the release version, you confirm you have the right to do so and that you understand how Valanti Labs intends to handle them (see the Privacy policy).' },
    { t: 'User responsibilities', b: 'You are responsible for following any professional medical or rehabilitation guidance you have received, and for stopping movement that causes pain or concerning symptoms.' },
    { t: 'Intellectual property', b: 'Valanti Labs, its name, branding, application, research outputs and underlying technology are the property of Valanti Labs Pty Ltd and protected by applicable laws.' },
    { t: 'Feedback and application output', b: 'Movement feedback produced by the application is supportive information only. It is not medical advice, diagnosis or treatment.' },
    { t: 'Professional-advice limitations', b: 'Valanti Labs is not a substitute for a doctor, physiotherapist, or other qualified professional. Always follow the advice of qualified professionals responsible for your care.' },
    { t: 'Health and safety limitations', b: 'You should not begin or continue an exercise that has been identified as unsuitable for you. If you experience concerning symptoms, stop and seek appropriate professional advice.' },
    { t: 'Privacy', b: 'Your use of Valanti Labs is also subject to our Privacy policy.' },
    { t: 'Third-party services', b: 'The application and website may rely on third-party services. Specific providers will be disclosed once confirmed.' },
    { t: 'Availability', b: 'Valanti Labs is in pre-release preparation. Availability, supported movements, and features may change as the application develops.' },
    { t: 'Suspension and termination', b: 'We may suspend or terminate access where these terms are breached, where there is a security, safety, legal or operational concern, or where required by law.' },
    { t: 'Warranties', b: 'The application is provided on an "as is" basis to the extent permitted by law. Specific warranty positions will be finalised by Australian legal review before publication.' },
    { t: 'Liability', b: 'Liability provisions will be drafted and reviewed by qualified Australian legal counsel and will not be applied in a way that excludes liability that cannot lawfully be excluded.' },
    { t: 'Governing law', b: 'These terms are intended to be governed by the laws of Australia. Final jurisdiction language will be confirmed before publication.' },
    { t: 'Contacting Valanti Labs', b: 'For questions about these terms, please use the contact form on this site.' },
  ],
};
